import type { DataSource, MutationOptions } from '@shared/innmaxLib/services'
import {
  convertData,
  QueryVariables,
  request,
  useMakeMutation,
} from '@shared/innmaxLib/services'
import { momentToString } from '@shared/innmaxLib/utils/webHelper'
import moment from 'moment'
import { isNil, path, pathOr } from 'ramda'
import React from 'react'
import { useQuery, UseQueryOptions } from 'react-query'
import * as converter from './app.converter'
import {
  useConverterAutoCharging,
  useConvertIBanner,
  useConvertIMissionReward,
  useConvertIMissionRewardList,
  useConvertIReserve,
  useConvertIRewardPoint,
  useConvertIRewardPointCouponList,
  useConvertIRewardPointList,
} from './app.converter'
import { AppBannerShelfStatus, useAppBannerTypeEnum } from './app.enum'
import { IAppImage, IAutoCharging, IRewardPoint } from './app.type'

export const useAppUsers = (query?: any, options?: UseQueryOptions) => {
  const { data, ...others } = useQuery(
    ['/app/mgm/account/{pageNum}/{pageSize}', query],
    {
      ...options,
    }
  )

  const dataSource = React.useMemo(() => {
    return convertData(
      {
        converter: (x: any) => ({
          ...x,
          statusStr: converter.convertAppUserStatusStr(x.status),
          createAtStr: momentToString(x.createAt),
        }),
      },
      data
    )
  }, [data])

  return {
    ...others,
    dataSource,
    loading: others.isLoading,
  }
}

export const useAppUser = ({ id }: { id: number }) => {
  const { data, refetch, isLoading, ...others } = useQuery([
    '/app/mgm/account/{id}',
    { id },
  ])

  const _data = React.useMemo(() => {
    return {
      ...pathOr({}, ['data'], data),
      statusStr: converter.convertAppUserStatusStr(
        path(['data', 'status'], data)
      ),
      authTypeStr: converter.convertAppUserAuthTypeStr(
        path(['data', 'authType'], data)
      ),
      createAtStr: momentToString(path(['data', 'createAt'], data)),
    }
  }, [data])

  return {
    ...others,
    refetch,
    loading: isLoading,
    data: _data as any,
  }
}

export const updateAppUser = (values: Partial<any> = {}) =>
  request('/app/mgm/account/{id}', {
    method: 'PUT',
    body: values,
  })

export const useUpdateAppUser = (options: MutationOptions = {}) => {
  return useMakeMutation(updateAppUser, options)
}

export const useAppUserPurchaseLog = (query: any) => {
  const { data, refetch, isLoading, ...others } = useQuery([
    '/app/mgm/charging/info/history/{pageNum}/{pageSize}',
    query,
  ])

  const dataSource = React.useMemo(() => {
    return convertData(
      {
        converter: converter.convertChargingHistory,
      },
      data
    )
  }, [data])

  return {
    ...others,
    refetch,
    loading: isLoading,
    dataSource,
  }
}

export const useAppUserLogDetail = (
  { id }: { id: number },
  options?: UseQueryOptions
) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    ['/app/mgm/charging/info/history/{id}', { id }],
    options
  )
  const _data: any = pathOr({}, ['data'], data)

  return {
    ...others,
    refetch,
    loading: isLoading,
    data: _data,
  }
}

export const useAppUserPointLog = (query: any) => {
  const { data, refetch, isLoading, ...others } = useQuery([
    '/app/mgm/rewardPoint/{pageNum}/{pageSize}',
    query,
  ])

  const dataSource = React.useMemo(() => {
    return convertData<IRewardPoint>(
      {
        converter: converter.convertRewardPoint,
      },
      data
    )
  }, [data])

  return {
    ...others,
    refetch,
    loading: isLoading,
    dataSource,
  }
}

export const useAppTasks = (query?: any, options?: UseQueryOptions) => {
  const { data, ...others } = useQuery(
    ['/app/mgm/mission/{pageNum}/{pageSize}', query],
    {
      ...options,
    }
  )

  const dataSource = React.useMemo(() => {
    return convertData(
      {
        converter: converter.convertTaskDetail,
      },
      data
    )
  }, [data])

  return {
    ...others,
    dataSource,
    loading: others.isLoading,
  }
}

export const useAppTask = ({ id }: { id?: number }) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    ['/app/mgm/mission/{id}', { id }],
    {
      enabled: !isNil(id),
    }
  )

  return {
    ...others,
    refetch,
    loading: isLoading,
    data: converter.convertTaskDetail(path(['data'], data)),
  }
}

export const useAppImage = ({
  type,
}: {
  type?: 'launch' | 'guide' | 'icon' | 'manuel'
}) => {
  const { data, refetch, ...others } = useQuery(
    ['/app/mgm/image/{type}', { type }],
    {
      enabled: !isNil(type),
    }
  )

  return {
    ...others,
    refetch,
    data: path(['data'], data) as any,
  }
}

export const updateImage = ({ type, ...values }: Partial<IAppImage> = {}) =>
  request(`/app/mgm/image/${type}`, { method: 'PUT', body: values })

export const useUpdateImage = (options: MutationOptions = {}) => {
  return useMakeMutation(updateImage, options)
}

export const useAppTerms = (query?: any, options?: UseQueryOptions) => {
  const { data, ...others } = useQuery(['/app/mgm/terms/{type}', query], {
    ...options,
  })

  return {
    ...others,
    data: path(['data'], data) as any,
  }
}

export const updateTerms = ({ type, ...values }: Partial<any> = {}) =>
  request(`/app/mgm/terms/${type}`, { method: 'PUT', body: values })

export const useUpdateTerms = (options: MutationOptions = {}) => {
  return useMakeMutation(updateTerms, options)
}

export const useUpdateTask = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/app/mgm/mission/{id}', {
        method: 'PUT',
        body: values,
      }),
    options
  )
}

export const useAddTask = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/app/mgm/mission', {
        method: 'POST',
        body: values,
      }),
    options
  )
}

export const useDeleteTask = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/app/mgm/mission/{id}', {
        method: 'DELETE',
        body: values,
      }),
    options
  )
}

export const useAppBannerList = (query?: any, options?: UseQueryOptions) => {
  const { data, ...others } = useQuery(
    ['/app/mgm/banner/{pageNum}/{pageSize}', query],
    {
      ...options,
    }
  )

  const { dataSource } = useConvertIBanner(data)

  return {
    ...others,
    dataSource,
  }
}

export const useAppBanner = (query?: any, options?: UseQueryOptions) => {
  const { data, ...others } = useQuery([`/app/mgm/banner/{id}`, query], {
    ...options,
    enabled: !!query?.id,
    cacheTime: 0,
  })

  const { byId: bannerTypeById } = useAppBannerTypeEnum()

  const dataSource = React.useMemo(() => {
    if (isNil(data)) {
      return {}
    }

    const _data = pathOr({}, ['data'], data) as any

    const shelfStatusStr =
      AppBannerShelfStatus.find(x => x.value === _data.status)?.label || ''

    const startAtDisplay = momentToString(_data.startAt, 'YYYY/MM/DD HH:mm:ss')
    const endAtDisplay = momentToString(_data.endAt, 'YYYY/MM/DD HH:mm:ss')

    const date = [moment(_data.startAt), moment(_data.endAt)]

    return {
      ..._data,
      shelfStatusStr,
      startAtDisplay,
      endAtDisplay,
      date,
      typeStr: bannerTypeById[_data.type]?.label || '',
    }
  }, [data])

  return {
    ...others,
    dataSource,
  }
}

export const createBanner = ({ ...values }: Partial<any> = {}) =>
  request(`/app/mgm/banner`, { method: 'POST', body: values })

export const useCreateBanner = (options: MutationOptions = {}) => {
  return useMakeMutation(createBanner, options)
}

export const updateBanner = ({ ...values }: Partial<any> = {}) =>
  request(`/app/mgm/banner/{id}`, { method: 'PUT', body: values })

export const useUpdateBanner = (options: MutationOptions = {}) => {
  return useMakeMutation(updateBanner, options)
}

export const useDeleteBanner = (options: MutationOptions = {}) => {
  return useMakeMutation(
    ({ ...values }: Partial<any> = {}) =>
      request(`/app/mgm/banner/{id}`, { method: 'DELETE', body: values }),
    options
  )
}

export const useAppOfficialLink = (query?: any, options?: UseQueryOptions) => {
  const { data, ...others } = useQuery(['/app/mgm/officialLink', query], {
    ...options,
  })

  const { dataSource } = useConvertIBanner(data)

  return {
    ...others,
    dataSource,
    data: path(['data'], data) as any,
  }
}

export const updateOfficialLink = ({ ...values }: Partial<any> = {}) =>
  request(`/app/mgm/officialLink`, { method: 'PUT', body: values })

export const useUpdateOfficialLink = (options: MutationOptions = {}) => {
  return useMakeMutation(updateOfficialLink, options)
}

export const useAppVersion = (query?: any, options?: UseQueryOptions) => {
  const { data, ...others } = useQuery(['/app/mgm/version', query], {
    ...options,
  })

  return {
    ...others,
    data: path(['data'], data) as any,
  }
}

export const updateVersion = ({ ...values }: Partial<any> = {}) =>
  request(`/app/mgm/version`, { method: 'PUT', body: values })

export const useUpdateVersion = (options: MutationOptions = {}) => {
  return useMakeMutation(updateVersion, options)
}

export const useAppReserveList = (query?: any, options?: UseQueryOptions) => {
  const { data, ...others } = useQuery(
    ['/app/mgm/charging/info/reserve/{pageNum}/{pageSize}', query],
    {
      ...options,
    }
  )

  const { dataSource } = useConvertIReserve(data)

  return {
    ...others,
    dataSource,
  }
}

export const useAppPersionalNotification = (
  query?: any,
  options?: UseQueryOptions
) => {
  const { data, ...others } = useQuery(
    ['/app/mgm/notification/personal/{pageNum}/{pageSize}', query],
    {
      ...options,
    }
  )

  const dataSource = React.useMemo(() => {
    return convertData(
      {
        converter: converter.convertPersonalNotification,
      },
      data
    )
  }, [data])

  return {
    ...others,
    dataSource,
    loading: others.isLoading,
  }
}

export const usePersionalNotification = ({ id }: { id?: number }) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    ['/app/mgm/notification/personal/{id}', { id }],
    {
      enabled: !isNil(id),
    }
  )

  const _data = React.useMemo(() => {
    return converter.convertPersonalNotification(path(['data'], data))
  }, [data])

  return {
    ...others,
    refetch,
    loading: isLoading,
    data: _data,
  }
}

export const useUpdatePersionalNotification = (
  options: MutationOptions = {}
) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/app/mgm/notification/personal/{id}', {
        method: 'PUT',
        body: values,
      }),
    options
  )
}

export const useAddPersionalNotification = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/app/mgm/notification/personal', {
        method: 'POST',
        body: values,
      }),
    options
  )
}

export const useDeletePersionalNotification = (
  options: MutationOptions = {}
) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/app/mgm/notification/personal/{id}', {
        method: 'DELETE',
        body: values,
      }),
    options
  )
}

export const useSystemNotification = (
  query?: any,
  options?: UseQueryOptions
) => {
  const { data, ...others } = useQuery(
    ['/app/mgm/notification/system/{pageNum}/{pageSize}', query],
    {
      ...options,
    }
  )

  const dataSource = React.useMemo(() => {
    return convertData(
      {
        converter: converter.convertSystemNotification,
      },
      data
    )
  }, [data])

  return {
    ...others,
    dataSource,
    loading: others.isLoading,
  }
}

export const useSystemNotificationDetail = ({ id }: { id?: number }) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    ['/app/mgm/notification/system/{id}', { id }],
    {
      enabled: !isNil(id),
    }
  )

  const _data = React.useMemo(() => {
    return converter.convertSystemNotification(path(['data'], data))
  }, [data])

  return {
    ...others,
    refetch,
    loading: isLoading,
    data: _data,
  }
}

export const useUpdateSystemNotification = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/app/mgm/notification/system/{id}', {
        method: 'PUT',
        body: values,
      }),
    options
  )
}

export const useAddSystemNotification = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/app/mgm/notification/system', {
        method: 'POST',
        body: values,
      }),
    options
  )
}

export const useDeleteSystemNotification = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/app/mgm/notification/system/{id}', {
        method: 'DELETE',
        body: values,
      }),
    options
  )
}

// Mission Reward management
//點數活動詳細資料
export const useMissionRewardList = (
  query?: any,
  options?: UseQueryOptions
) => {
  const { data, ...others } = useQuery(
    ['/app/mgm/mission/{pageNum}/{pageSize}', query],
    {
      ...options,
    }
  )

  const { dataSource } = useConvertIMissionRewardList(data)

  return {
    ...others,
    dataSource,
  }
}

export const useMissionReward = (query?: any, options?: UseQueryOptions) => {
  const { data: _data, ...others } = useQuery(
    ['/app/mgm/mission/{id}', query],
    {
      enabled: !!query?.id,
      ...options,
      select: (res: any) => path(['data'], res) as any,
    }
  )

  const dataSource = useConvertIMissionReward(_data)

  return {
    ...others,
    data: dataSource,
    loading: others.isLoading,
  }
}

export const useAddMissionReward = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/app/mgm/mission', {
        method: 'POST',
        body: values,
      }),
    options
  )
}

export const useUpdateMissionReward = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/app/mgm/mission/{id}', {
        method: 'PUT',
        body: values,
      }),
    options
  )
}

export const useDeleteMissionReward = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/app/mgm/mission/{id}', {
        method: 'DELETE',
        body: values,
      }),
    options
  )
}

// Reward point management
// reward-point-mgm-controller : 點數相關API
// App序號管理
export const useRewardPointList = (query?: any, options?: UseQueryOptions) => {
  const { data, ...others } = useQuery(
    ['/app/mgm/rewardPoint/coupon/{pageNum}/{pageSize}', query],
    {
      ...options,
    }
  )

  const { dataSource } = useConvertIRewardPointList(data)

  return {
    ...others,
    dataSource,
  }
}

export const useRewardPoint = (query?: any, options?: UseQueryOptions) => {
  const { data: _data, ...others } = useQuery(
    ['/app/mgm/rewardPoint/coupon/{id}', query],
    {
      enabled: !!query?.id,
      ...options,
      select: (res: any) => path(['data'], res) as any,
    }
  )

  const dataSource = useConvertIRewardPoint(_data)

  return {
    ...others,
    data: dataSource,
    loading: others.isLoading,
  }
}

export const useRewardPointCouponList = (
  query?: any,
  options?: UseQueryOptions
) => {
  const { data: _data, ...others } = useQuery(
    ['/app/mgm/rewardPoint/coupon/{id}/list', query],
    {
      enabled: !!query?.id,
      ...options,
      select: (res: any) => path(['data'], res) as any,
    }
  )

  const dataSource = useConvertIRewardPointCouponList(_data)

  return {
    ...others,
    data: dataSource,
    loading: others.isLoading,
  }
}

export const useAddRewardPoint = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/app/mgm/rewardPoint/coupon', {
        method: 'POST',
        body: values,
      }),
    options
  )
}

export const useUpdateRewardPoint = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/app/mgm/rewardPoint/coupon/{id}', {
        method: 'PUT',
        body: values,
      }),
    options
  )
}

export const useDeleteRewardPoint = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/app/mgm/rewardPoint/coupon/{id}', {
        method: 'DELETE',
        body: values,
      }),
    options
  )
}

export const useRewardPointExport = (query?: any, options?: QueryVariables) => {
  const [enabled, setIsEnabled] = React.useState(false)

  const { isLoading } = useQuery(
    ['/app/mgm/rewardPoint/coupon/{id}/export', { ...query }],
    {
      ...options,
      enabled,
      onSuccess: (response: any) => {
        if (options?.onSuccess) {
          options.onSuccess(response)
        }
        setIsEnabled(false)
      },
      onError: error => {
        console.log(error)
        setIsEnabled(false)
      },
    }
  )

  return [
    isLoading,
    () => {
      setIsEnabled(true)
    },
  ] as [boolean, () => void]
}

export const useAutoChargingList = (query?: any, options?: UseQueryOptions) => {
  const { data: _data, ...others } = useQuery(
    ['/evccid/list/{pageNum}/{pageSize}', query],
    options
  )

  const converter = useConverterAutoCharging()

  const dataSource: DataSource<IAutoCharging> = React.useMemo(() => {
    return convertData(
      {
        converter,
      },
      _data
    )
  }, [_data, converter])

  return {
    ...others,
    dataSource,
    loading: others.isLoading,
  }
}

export const useAutoChargingExport = (
  query?: any,
  options?: QueryVariables
) => {
  const [enabled, setIsEnabled] = React.useState(false)

  const { isLoading } = useQuery(['/evccid/list/export', { ...query }], {
    ...options,
    enabled,
    onSuccess: (response: any) => {
      if (options?.onSuccess) {
        options.onSuccess(response)
      }
      setIsEnabled(false)
    },
    onError: error => {
      console.log(error)
      setIsEnabled(false)
    },
  })

  return [
    isLoading,
    () => {
      setIsEnabled(true)
    },
  ] as [boolean, () => void]
}

export const useAutoChargingById = (query?: any, options?: UseQueryOptions) => {
  const { data: _data, ...others } = useQuery(['/evccid/{id}', query], {
    enabled: !!query?.id,
    ...options,
  })

  const converter = useConverterAutoCharging()

  const data = React.useMemo(() => {
    return converter(path(['data'], _data))
  }, [_data, converter])

  return {
    ...others,
    data,
    loading: others.isLoading,
  }
}

export const useEditAutoCharging = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/evccid/{id}', {
        method: 'PUT',
        body: values,
      }),
    options
  )
}

export const useAutoChargingHistory = (
  query?: any,
  options?: UseQueryOptions
) => {
  const { data, ...others } = useQuery(
    ['/evccid/historyOrder/{id}/{pageNum}/{pageSize}', query],
    {
      enabled: !!query?.id,
      ...options,
    }
  )

  const _converter = converter.useConverterAutoChargingPurchaseHistory()

  const dataSource = React.useMemo(() => {
    return convertData(
      {
        converter: _converter,
      },
      data
    )
  }, [data, _converter])

  return {
    ...others,
    dataSource,
    loading: others.isLoading,
  }
}
